/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState, useContext } from 'react';
import { createForm } from 'rc-form';
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { ConnectedProps, DefaultRootState, connect } from 'react-redux';
import { match } from 'react-router-dom';
import { goBack } from 'connected-react-router';
import { PowerBIEmbed } from 'powerbi-client-react';
import { SectionVisibility, BackgroundType, TokenType } from 'powerbi-models';
import { Report, Page } from 'powerbi-client';

import { OrganizationId } from 'yeself-common/typings/application/Application';

import { selectMemberOrganizationsList } from 'Console/data/organizations/OrganizationSelector';

import { FooterColorContext } from 'Console/components/FooterColourContext';
import Loading from 'Console/components/common/loading/Loading';

import { pushNotificationToStack } from 'Console/data/notification/actions';
import {
    fetchReportConfigurationRequest,
    resetAnalyticsState,
} from 'Console/data/analytics/AnalyticsActions';
import {
    selectReportConfiguration,
    selectReportConfigurationHasFailed,
    selectReportConfigurationIsLoading,
} from 'Console/data/analytics/AnalyticsSelectors';

import ReportPagesSidebar from './ReportPagesSidebar';

const styles = () =>
    createStyles({
        root: {
            display: 'flex',
        },
        content: {
            flexGrow: 1,
        },
        reportFrame: {
            height: '85vh',
            width: '100%',
            '& iframe': {
                border: 'none',
            },
        },
    });

interface PowerBiEmbedProps extends WithStyles<typeof styles>, WrappedComponentProps {
    match: match<{ organizationId: OrganizationId }>;
}

const mapStateToProps = (state: DefaultRootState) => ({
    reportConfiguration: selectReportConfiguration(state),
    hasFailed: selectReportConfigurationHasFailed(state),
    isLoading: selectReportConfigurationIsLoading(state),
    organizationsList: selectMemberOrganizationsList(state),
});

const connector = connect(mapStateToProps, {
    fetchReportConfigurationRequest,
    resetAnalyticsState,
    pushNotificationToStack,
    goBack,
});

type ConnectedAnalyticsReportEmbedProps = ConnectedProps<typeof connector> & PowerBiEmbedProps;

const AnalyticsReportEmbed = ({
    fetchReportConfigurationRequest,
    resetAnalyticsState,
    pushNotificationToStack,
    goBack,
    match,
    classes,
    reportConfiguration,
    hasFailed,
    isLoading,
    organizationsList,
}: ConnectedAnalyticsReportEmbedProps): JSX.Element => {
    const [report, setReport] = useState<Report>();
    const [pages, setPages] = useState<Page[]>([]);
    const [isReportLoaded, setIsReportLoaded] = useState(false);
    const [activePage, setActivePage] = useState<Page>();

    useEffect(() => {
        const organizationId = match?.params?.organizationId;
        if (organizationId === undefined) {
            return;
        }

        fetchReportConfigurationRequest(organizationId);
    }, [fetchReportConfigurationRequest, match]);

    const organizationName = organizationsList?.find(
        (o: { id: OrganizationId }) => o.id === match?.params?.organizationId
    )?.name;

    useEffect(() => {
        if (!isReportLoaded || !report || !reportConfiguration.report.accessToken) {
            return;
        }

        report.setAccessToken(reportConfiguration.report.accessToken);
    }, [reportConfiguration]);

    useEffect(() => {
        if (report && isReportLoaded) {
            report.getPages().then((pages) => {
                const visiblePages = pages.filter(
                    (p: Page) =>
                        !reportConfiguration.excludedPages.includes(p.displayName) &&
                        p.visibility === SectionVisibility.AlwaysVisible
                );

                setPages(visiblePages);

                const activePage = visiblePages.find((p) => p.isActive);

                setActivePage(activePage);
            });
        }
    }, [report, isReportLoaded]);

    useEffect(() => {
        if (report && activePage?.name) {
            report.setPage(activePage.name);
        }
    }, [activePage]);

    useEffect(() => {
        if (hasFailed && !isLoading) {
            pushNotificationToStack(
                <FormattedMessage
                    id="Notification.report.notConfigured"
                    defaultMessage="No analytics reports configured - contact your administrator for more information"
                />
            );
            goBack();
        }
    }, [hasFailed, isLoading]);

    useEffect(
        () => () => {
            resetAnalyticsState();
        },
        []
    );

    const { setFooterColor } = useContext(FooterColorContext);

    useEffect(() => {
        setFooterColor('#191E25');
        document.body.style.background = '#191E25';

        return () => {
            setFooterColor('#FAFAFA');
            document.body.style.background = '#FAFAFA';
        };
    }, [setFooterColor]);

    const changeActivePage = (page: Page) => {
        setActivePage(page);
    };

    if (isLoading || !reportConfiguration.report.accessToken) {
        return <Loading minHeight={300} />;
    }

    return (
        <div className={classes.root}>
            <ReportPagesSidebar
                pages={pages}
                activePage={activePage}
                organizationName={organizationName}
                setActivePage={changeActivePage}
            />
            <main className={classes.content}>
                <PowerBIEmbed
                    embedConfig={{
                        ...reportConfiguration.report,
                        tokenType: TokenType.Embed,
                        settings: {
                            panes: {
                                filters: {
                                    expanded: false,
                                    visible: true,
                                },
                                pageNavigation: {
                                    visible: false,
                                },
                            },
                            background: BackgroundType.Default,
                        },
                    }}
                    eventHandlers={
                        new Map([
                            [
                                'loaded',
                                () => {
                                    setIsReportLoaded(true);
                                },
                            ],
                        ])
                    }
                    cssClassName={classes.reportFrame}
                    getEmbeddedComponent={(embeddedReport) => {
                        setReport(embeddedReport as Report);
                    }}
                />
            </main>
        </div>
    );
};

export const ConnetedAnalyticsReportEmbed = withStyles(styles)(
    injectIntl(connector(createForm()(AnalyticsReportEmbed)))
);
